import React, { useMemo, useRef } from 'react';
import { NodeProps } from 'reactflow';
import { twMerge } from 'tailwind-merge';

import { InteractionDagNode } from '../../dag/internalDag';
import { DbtDagNode, getLensInfo, LensDisplayInfo } from '../../dbt-dag';
import { useResourceNodeColors } from '../../hooks/useResourceNodeColor';
import { useDagContext } from '../../providers/DagProvider';
import { useInternalDagContext } from '../../providers/InternalDagProvider';
import { AutoExposureIcon } from '../AutoExposureIcon';
import { BaseNodeComponent } from '../BaseNode';
import { LensNodeBadge } from '../LensNodeBadge';
import { LensResourceIcon } from '../LensResourceIcon';
import { getResourceType, ResourceTypeExplorer } from '../ResourceTypeIcon';
import { Annotation } from './Annotation';
import { NODE_HEIGHT, NODE_HEIGHT_CLASS } from './getNodeBoundingBox';
import { MainLabel } from './MainLabel';
import { getSublabel, Sublabel } from './Sublabel';
import { ZoomedOutNode } from './ZoomedOutNode';
export { getNodeBoundingBox } from './getNodeBoundingBox';

type LensNodeDisplayProps<TDbtDagNode extends DbtDagNode> = {
  isSelected: boolean;
  resourceType: ResourceTypeExplorer | 'unknown';
  enableNodeBackgroundColor: boolean;
} & NodeProps<InteractionDagNode<TDbtDagNode>>;

const LensNodeImpl = <TDbtDagNode extends DbtDagNode>({
  isSelected,
  resourceType,
  enableNodeBackgroundColor,
  ...props
}: LensNodeDisplayProps<TDbtDagNode>) => {
  const { lens, lensContext } = useDagContext();
  const lensInfo: Partial<LensDisplayInfo> | null = useMemo(() => {
    return getLensInfo(lens, props.data, lensContext);
  }, [lens, props.data, lensContext]);
  const showColor = !lens || !!lensInfo?.color;

  const { grain } = useInternalDagContext();
  const shouldRenderColumn =
    grain === 'column' && !['project', 'error'].includes(resourceType);
  const displayResourceType = shouldRenderColumn ? 'column' : resourceType;
  const border = isSelected
    ? 'box-content border-2 border-borderMainActive -m-[1px]'
    : 'box-content border border-borderMain';

  const baseClassname = twMerge(
    'cursor-pointer overflow-hidden rounded-md border border-solid border-borderMain text-base text-fgMain',
    border,
  );

  const backgroundColorClass = useResourceNodeColors({
    resourceType: displayResourceType,
    showColor: false,
  });

  const shouldShowMetadata = useMemo(() => {
    // Check if LensNodeBadge will render
    const willShowBadge = !!lensInfo;

    // Check if Sublabel will render
    const { sublabel, access } = getSublabel(props.data);
    const isExternalProject =
      props.data.isExternalNode && props.data.resourceType === 'project';
    const isExternalResource =
      props.data.isExternalNode && props.data.resourceType !== 'project';
    const willShowSublabel =
      isExternalResource ||
      (isExternalProject && access === 'no_permission') ||
      sublabel ||
      shouldRenderColumn;

    return willShowBadge || willShowSublabel;
  }, [lensInfo, props.data, shouldRenderColumn]);

  const { autoBiProvider: autoExposureBiProvider } = props.data;
  return (
    <>
      <BaseNodeComponent {...props} hoverYOffset={NODE_HEIGHT} hoverXOffset={10}>
        {({ isHovered, isMagnified, disableMagnifiedNode }) => {
          return (
            <>
              <div
                className={twMerge(baseClassname, backgroundColorClass)}
                {...(!isMagnified && {
                  'data-testid': 'node-bg',
                  'data-resourcetype': displayResourceType,
                  ...(autoExposureBiProvider
                    ? { 'data-auto-exposure-bi-provider': autoExposureBiProvider }
                    : {}),
                })}
              >
                <div
                  className={twMerge(
                    NODE_HEIGHT_CLASS,
                    'flex items-center justify-center space-x-4 text-sm font-normal',
                    'flex-col text-fgMain',
                  )}
                >
                  <div className="flex w-full flex-1 flex-row items-center space-x-4 overflow-hidden p-4">
                    {!autoExposureBiProvider && (
                      <LensResourceIcon
                        resourceType={displayResourceType}
                        className="h-10 w-10 rounded"
                        isSelected={isSelected}
                        showColor={showColor}
                      />
                    )}

                    {!!autoExposureBiProvider && (
                      <AutoExposureIcon biProvider={autoExposureBiProvider} />
                    )}
                    <div className="flex flex-col items-center">
                      <Annotation {...props} />
                      <MainLabel {...props} />
                    </div>
                  </div>
                  {shouldShowMetadata && (
                    <div
                      className="!m-0 w-full overflow-hidden border-t border-borderMain bg-bgDisabled px-4"
                      data-testid="metadata-section"
                    >
                      <Sublabel
                        {...props}
                        resourceType={resourceType}
                        shouldRenderColumn={shouldRenderColumn}
                      />
                      <LensNodeBadge
                        isMagnified={isMagnified}
                        disableMagnifiedNode={disableMagnifiedNode}
                        data={props.data}
                        resourceType={resourceType}
                        className="absolute -top-5 right-2"
                      />
                    </div>
                  )}
                </div>
                {!isMagnified && !disableMagnifiedNode && (
                  <ZoomedOutNode
                    baseClassname={baseClassname}
                    innerClassname={NODE_HEIGHT_CLASS}
                    resourceType={displayResourceType}
                    isSelected={isSelected}
                    isHovered={isHovered}
                    data={props.data}
                  />
                )}
              </div>
            </>
          );
        }}
      </BaseNodeComponent>
    </>
  );
};

export const LensNodeDisplay = React.memo(LensNodeImpl);

type LensNodeProps<TDbtDagNode extends DbtDagNode> = NodeProps<
  InteractionDagNode<TDbtDagNode>
>;

const LensNodeContainer = <TDbtDagNode extends DbtDagNode>(
  props: LensNodeProps<TDbtDagNode>,
) => {
  const { data } = props;
  const { resourceType: unvalidatedResourceType, id } = data;

  const resourceType = getResourceType(unvalidatedResourceType, 'unknown');

  const dagContext = useDagContext();
  const isSelected = dagContext.selectedNode?.id === id || data.isSelected;

  const wasSelectedRef = useRef(isSelected);
  wasSelectedRef.current = wasSelectedRef.current || isSelected;

  const { lens } = useDagContext();

  const isLensEnabled = !!lens;
  const renderMaterializedModel =
    lens === 'materialization' && resourceType === 'model';
  const enableNodeBackgroundColor = !isLensEnabled || renderMaterializedModel;

  return (
    <>
      <LensNodeDisplay
        {...props}
        isSelected={isSelected}
        resourceType={resourceType}
        enableNodeBackgroundColor={enableNodeBackgroundColor}
      />
    </>
  );
};

export const LensNode = React.memo(LensNodeContainer);
